import type { HtmlPortalNode } from "react-reverse-portal";

import { dequal } from "dequal";
import type { LngLatLike } from "mapbox-gl";
import type { ISelectedWell } from "stores/map";
import { create } from "zustand";

import type { IChartPreset } from "models/chartPreset";
import type { INormalizeByInput } from "models/normalizeBy";

export type WellCardPanelTab = "Summary" | "Production" | "Gunbarrel" | "Details";
export type GunbarrelView = "xda" | "3d";

/**
 * The card's three embedded widgets - the ones that have a screenshot mode of
 * their own, and so a fullscreen state of their own.
 */
export type WellCardEmbed = "chart" | "xda" | "3d";

/** Tab order, and the only tab names a persisted session entry may name. */
export const WELL_CARD_PANEL_TABS: readonly WellCardPanelTab[] = [
  "Summary",
  "Production",
  "Gunbarrel",
  "Details"
];
export const GUNBARREL_VIEWS: readonly GunbarrelView[] = ["xda", "3d"];

/** Where the card has been dragged to, as its top-left corner in px. */
export interface PanelPosition {
  readonly x: number;
  readonly y: number;
}

/**
 * The card's default width, and the narrowest the user may drag it to.
 *
 * Set by the widest thing the card has to show without truncating: the
 * Production Array's eight headers, each carrying the unit the response sent.
 * Measured in Chrome against the real header components - the six product
 * columns share what is left after the two fixed ones, and the longest header
 * an imperial org sees ("Water (bbl/d)") needs 840px of card, while a metric
 * org's ("Gas (e3m3/d)") needs 864. This covers both with a little slack.
 *
 * It is the floor as well as the default because everything below it is a
 * truncated table: the card may be widened for a longer chart, never narrowed
 * past what its own content needs.
 */
export const PANEL_WIDTH = 868;
/** The default card's distance from the top of the window, plus its bottom gap. */
export const PANEL_VERTICAL_MARGIN = 72;
/** The gap the card keeps from the edges of the window. */
export const PANEL_GUTTER = 14;
/**
 * The shortest the card is allowed to be. A window can always be made shorter
 * than any card worth showing, and the card has to stay visible and usable at
 * every size: below this it stops shrinking and hangs over the bottom edge
 * instead, which leaves its title bar and controls in reach.
 *
 * The title bar, the header band and the tab strip take about 200px before any
 * content, so a floor near that leaves a card that is all chrome. This one
 * keeps a tab worth reading underneath them.
 */
export const PANEL_MIN_HEIGHT = 480;

const cssPx = (name: string, fallback: number): number => {
  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  const value = parseFloat(raw);
  return Number.isFinite(value) ? value : fallback;
};

/**
 * The app chrome the card has to stay out of: the top navigation bar and the
 * left activity bar. Both belong to the application rather than to the
 * dashboard, so the card gives way to them — and a title bar slid under the
 * navigation bar cannot be grabbed again, which is what made dragging the card
 * upwards feel broken.
 *
 * The activity bar is measured rather than read from its variable: the
 * variable holds its collapsed width, and it is wider whenever a pane is open.
 */
const chromeInset = (): { readonly left: number; readonly top: number } => {
  const rail = document.querySelector(".activity-bar");
  return {
    left: rail ? rail.getBoundingClientRect().width : cssPx("--activity-bar-width", 60),
    top: cssPx("--navbar-height", 45)
  };
};

/**
 * Keeps the card on screen and clear of the chrome — not just its corner. A
 * card dragged half off the bottom would take its own controls with it, and
 * the map behind it is not what the user was reading.
 *
 * Takes no measurement of the card: the width it intends is passed in and the
 * least height it may be squeezed to is known here, and reading the rendered
 * box instead coupled the clamp to a size the clamp itself decides — a card
 * shrunk by one position then licensed the next one along.
 *
 * The width is a parameter because the user sets it. A card widened to half
 * the screen and then clamped against the default would have been licensed a
 * position that put its right-hand half past the edge of the window.
 */
export const clampPanelPosition = (
  position: PanelPosition,
  width: number
): PanelPosition => {
  const chrome = chromeInset();
  // The near edge always wins: `Math.max(min, ...)` on the far bound means the
  // range can never invert on a window smaller than the card, which would
  // otherwise flip the sign of the clamp and throw the card off screen.
  const axis = (value: number, min: number, extent: number, span: number) =>
    Math.min(Math.max(value, min), Math.max(min, extent - span));
  return {
    /*
     * Horizontally the card keeps its width, so the whole of it has to fit:
     * measured against the width it INTENDS rather than the width it happens
     * to have, because a card squeezed by a narrow window would otherwise
     * license a position that squeezed it further.
     */
    x: axis(position.x, chrome.left, window.innerWidth, width),
    /*
     * Vertically the card SHRINKS as it is dragged down rather than keeping a
     * fixed height, so what has to fit below its top edge is the shortest card
     * allowed, not the one being dragged. That is also what gives the drag its
     * vertical range: clamped against the card's full height there were only a
     * few pixels of travel.
     */
    y: axis(position.y, chrome.top, window.innerHeight, PANEL_MIN_HEIGHT + PANEL_GUTTER)
  };
};

/**
 * What the window leaves for the card beside the application's chrome, which
 * is the widest it can be drawn. `leftBound` is the card's own left limit, the
 * one a maximized card starts at.
 */
export const panelRoomInWindow = (leftBound: number): number =>
  Math.max(PANEL_WIDTH, window.innerWidth - leftBound - PANEL_GUTTER);

/**
 * The width the card is really drawn at: its own width until the window has no
 * room for that, where the card's CSS caps it with a `min()`.
 *
 * This is the width the clamp has to be given, not the stored one. A card
 * widened and then given less room - opening a pane beside it is enough - is
 * drawn narrower than it is stored at, and a clamp told the stored width finds
 * no horizontal range at all: both of its bounds land on the left edge of the
 * chrome, so every drag pins the card there and it cannot be moved sideways
 * again until the user narrows it.
 */
export const effectivePanelWidth = (width: number, leftBound: number): number =>
  Math.min(width, panelRoomInWindow(leftBound));

/** Screen anchor for the map preview popup, plus the wells it describes. */
export interface WellCardPreview {
  /**
   * Every well the click landed on, in the order the map returned them, which
   * is the order they are drawn. Wells bunch up: at most zooms a single click
   * covers several, and one of them is not obviously the one the user meant,
   * so the popup pages through them rather than picking for him.
   *
   * The map's own selection records, not just uwis — paging re-selects the
   * well it shows, and that write carries the colour and label the click's
   * write did.
   */
  readonly candidates: readonly ISelectedWell[];
  /** Which candidate the popup is showing. */
  readonly activeIndex: number;
  readonly point: { readonly x: number; readonly y: number };
  /** Where the well is, so the popup can follow it as the camera moves. */
  readonly lngLat: LngLatLike;
  /**
   * Each candidate's extent by uwi, so the popup can sit clear of the whole
   * well and not just the clicked point. Kept off the candidates, which paging
   * writes into the map's selection.
   */
  readonly wellBounds?: Readonly<Record<string, WellBounds>>;
}

/** `[[west, south], [east, north]]`, in degrees. */
export type WellBounds = [[number, number], [number, number]];

/** The well the popup is describing, or null when there is no popup. */
export const previewUwi = (preview: Maybe<WellCardPreview>): Maybe<string> =>
  preview?.candidates[preview.activeIndex]?.Uwi ?? null;

/**
 * The half-width the panel asks map-svc for while the user has set none, one
 * default per display unit. Both read best across the wells they were tried
 * on, and each is a round number in the unit it is shown in: converting the
 * metric one is what put 3,281 ft in front of an imperial user.
 *
 * Which one applies is decided where the display unit is known, by
 * `useLineHalfLengthMeters`. The service's own default half-length of 100 m
 * each side is what every other caller keeps, because `getPerpendicularLine`
 * omits the query string when no value is passed — not because of these.
 */
export const DEFAULT_LINE_HALF_LENGTH_METERS = 1000;
export const DEFAULT_LINE_HALF_LENGTH_FEET = 3000;
export const MIN_LINE_HALF_LENGTH_METERS = 100;
export const MAX_LINE_HALF_LENGTH_METERS = 10000;

/**
 * The slice the card remembers per dashboard, for as long as the tab lives.
 *
 * Held in memory only. A refresh is a clean slate - no card, open or docked -
 * which is how Insights behaves and what the card is expected to do. What it
 * does keep is the session: leave a dashboard with the card open and dragged
 * somewhere, come back to it, and it is where you left it.
 */
export interface WellCardPanelRememberedState {
  readonly open: boolean;
  readonly maximized: boolean;
  /**
   * The card is docked as a bar along the foot of the screen. Kept apart from
   * `maximized` rather than folded into one mode: restoring has to put the
   * card back the way it was, maximized or not.
   */
  readonly minimized: boolean;
  readonly activeTab: WellCardPanelTab;
  readonly gunbarrelView: GunbarrelView;
  /**
   * The half-width the user settled on, or null while none has been settled on.
   *
   * Null rather than a number, so "untouched" and "happens to equal the
   * default" stay apart: the default depends on the display unit, and a card
   * that had already stored 1,000 m could not be told from one that had never
   * been touched by an imperial user.
   */
  readonly lineHalfLengthMeters: Maybe<number>;
  /**
   * The well the panel is showing. Deliberately not a mirror of
   * `selectedWells`: it advances on View Well Card and on well-list /
   * data-table row clicks, and survives the selection being cleared. It is
   * part of the slice because a card is about one well: a dashboard the user
   * returns to has to come back showing the well he left on it, not empty.
   */
  readonly targetUwi: Maybe<string>;
  /**
   * Where the user dragged the card to, or null while it has never been
   * dragged — which is the design's top-right placement, expressed in CSS
   * rather than as numbers here.
   */
  readonly position: Maybe<PanelPosition>;
  /**
   * How wide the user dragged the card's edges out to, in px.
   *
   * A number rather than a nullable one, unlike `position`: the default width
   * is a constant and not a placement the CSS works out, so there is nothing
   * for "untouched" to mean here that `PANEL_WIDTH` does not already say.
   */
  readonly panelWidth: number;
  /**
   * How tall the user dragged the card's bottom edge to, in px, or null while
   * he never has.
   *
   * Nullable unlike the width, and for the same reason `position` is: an
   * untouched card has no height of its own. It runs from wherever its top
   * sits down to the window's bottom gutter, which is a sum the CSS does and
   * redoes as the window changes. A number written here instead would be a
   * card that had stopped following the foot of the window before the user
   * ever asked it to.
   */
  readonly panelHeight: Maybe<number>;
}

export interface WellCardPanelState extends WellCardPanelRememberedState {
  /** Dashboard the slice above belongs to. */
  readonly dashboardId: string;
  /** Map preview popup. Never persisted — a popup must not survive a reload. */
  readonly preview: Maybe<WellCardPreview>;
  /** The popup is hidden because its well is panned out of view. */
  readonly previewOffScreen: boolean;
  /**
   * The card's well has given its map outline up, to a preview on another well
   * or to Escape. It stays off until the card is pointed at a well again, so a
   * well the user moved on from does not light up when the selection clears.
   */
  readonly outlineReleased: boolean;
  /** Bumped to ask the map's line tool to start a draw. */
  readonly drawLineRequestId: number;
  /** Bumped to ask the map's line tool to stand an armed draw back down. */
  readonly drawLineCancelId: number;
  /**
   * The map's line tool is armed and waiting for the two clicks. Written by
   * `XdaLineMarker`, which owns that mode, so the panel's draw button lights
   * up while a draw is in progress the same way the map's own toggle does.
   */
  readonly drawArmed: boolean;
  /** True while a user-drawn line is what the gunbarrel is showing. */
  readonly customLineActive: boolean;
  /**
   * Which embedded widget is in screenshot mode, and the portal carrying it.
   *
   * A dashboard widget answers its screenshot by going fullscreen: the pane
   * moves it into a sheet over the grid. The card's embeds have to do the
   * same, or the shot is whatever fits inside an 868px card. The portal is
   * what moves them without unmounting, so the chart keeps its series and the
   * viewers keep their scenes across the move and back.
   *
   * Live state, never persisted: a reload owes the user a card, not a sheet.
   */
  readonly screenshotEmbed: Maybe<WellCardEmbed>;
  readonly screenshotNode: Maybe<HtmlPortalNode>;
  /**
   * The card chart's normalize input, published from inside its settings
   * provider so the Production Array can send the same one. Null while the
   * chart is unmounted or not normalized.
   */
  readonly chartNormalizeBy: Maybe<INormalizeByInput>;
  /**
   * The preset the card's chart was last left on, so a card closed and opened
   * again, or remounted after a well with no production, comes back on it.
   * One for the whole session rather than per dashboard, and gone on refresh
   * and sign-out like the rest of the card, which is how Insights behaves.
   */
  readonly chartPreset: Maybe<IChartPreset>;
}

export interface WellCardPanelActions {
  /** Bring up the card this dashboard was left with, if the tab still has it. */
  readonly hydrate: (dashboardId: string) => void;
  readonly setOpen: (open: boolean) => void;
  readonly toggleMaximized: () => void;
  readonly setMinimized: (minimized: boolean) => void;
  /**
   * Bring the card back out of the dock.
   *
   * `maximized` says how it comes back, and each way into this has its own
   * answer: the bar's own restore leaves the card as it was, its fullscreen
   * button brings it back filling the pane, and a drag brings it back at its
   * own size, because the drag is on its way to placing it. Omit the argument
   * to keep whatever it was.
   *
   * One write rather than a pair, so the card cannot be painted half restored:
   * state written from a pointer handler is not batched here, and a card that
   * cleared `minimized` first would fill the screen for a frame on its way out
   * of a maximized dock.
   */
  readonly undock: (maximized?: boolean) => void;
  readonly setPosition: (position: Maybe<PanelPosition>) => void;
  /**
   * Committed when a resize handle is released, not on every move: the width
   * under the pointer is the panel's own local state, and a write per move
   * would re-render every reader of the card.
   *
   * Held above `PANEL_WIDTH` here. The upper bound belongs to whoever is
   * dragging, because it is the window's, and the caller is the one that knows
   * how much of the window the application's chrome has taken.
   */
  readonly setPanelWidth: (width: number) => void;
  /**
   * Committed when the bottom handle is released, the same way the width is,
   * and held above `PANEL_MIN_HEIGHT` here.
   *
   * The upper bound is the dragger's, and more so than the width's: what is
   * left below the card is set by where the card's top edge sits, which this
   * store is not the one measuring.
   */
  readonly setPanelHeight: (height: number) => void;
  readonly setActiveTab: (tab: WellCardPanelTab) => void;
  readonly setGunbarrelView: (view: GunbarrelView) => void;
  readonly setLineHalfLengthMeters: (meters: number) => void;
  readonly setTargetUwi: (uwi: Maybe<string>) => void;
  readonly setPreview: (preview: Maybe<WellCardPreview>) => void;
  readonly setPreviewOffScreen: (offScreen: boolean) => void;
  readonly setPreviewIndex: (index: number) => void;
  readonly releaseOutline: () => void;
  readonly requestLineDraw: () => void;
  /**
   * Stand the pending two-point draw down. The map owns that mode, so the
   * card asks for it here rather than reaching into the draw control.
   */
  readonly cancelLineDraw: () => void;
  readonly setDrawArmed: (armed: boolean) => void;
  readonly setCustomLineActive: (active: boolean) => void;
  /**
   * Claim the card's fullscreen sheet for one embed, or give it up.
   *
   * Pass the embed's portal to claim it and `null` to release it. A release
   * only counts from whichever embed is holding the sheet, so an embed
   * unmounting after another one has taken it cannot pull the sheet out from
   * under the new holder.
   */
  readonly setScreenshotEmbed: (
    embed: WellCardEmbed,
    node: Maybe<HtmlPortalNode>
  ) => void;
  readonly setChartNormalizeBy: (normalizeBy: Maybe<INormalizeByInput>) => void;
  readonly setChartPreset: (preset: Maybe<IChartPreset>) => void;
  readonly reset: () => void;
}

export type WellCardPanelStore = WellCardPanelState & WellCardPanelActions;

const defaultRememberedState: WellCardPanelRememberedState = {
  open: false,
  maximized: false,
  minimized: false,
  activeTab: "Summary",
  gunbarrelView: "xda",
  lineHalfLengthMeters: null,
  targetUwi: null,
  position: null,
  panelWidth: PANEL_WIDTH,
  panelHeight: null
};

const initialState: WellCardPanelState = {
  ...defaultRememberedState,
  dashboardId: "",
  preview: null,
  previewOffScreen: false,
  outlineReleased: false,
  drawLineRequestId: 0,
  drawLineCancelId: 0,
  drawArmed: false,
  customLineActive: false,
  screenshotEmbed: null,
  screenshotNode: null,
  chartNormalizeBy: null,
  chartPreset: null
};

/**
 * Hold a width to the narrowest card worth showing, and nothing else.
 *
 * Only the floor is here. The ceiling is the window minus the chrome beside
 * the card, which the panel's own CSS already imposes with a `min()` — putting
 * a second copy of that sum here is what would let the two drift apart.
 */
export const clampPanelWidth = (width: number): number =>
  Number.isFinite(width) ? Math.max(PANEL_WIDTH, width) : PANEL_WIDTH;

/**
 * Hold a height to the shortest card worth showing, and nothing else.
 *
 * Only the floor, as above. The ceiling is what the window leaves below the
 * card's top edge, and that is a sum this store cannot do: it moves with the
 * window AND with wherever the card has been dragged to, so the handle that
 * knows both is the one that imposes it.
 */
export const clampPanelHeight = (height: number): number =>
  Number.isFinite(height) ? Math.max(PANEL_MIN_HEIGHT, height) : PANEL_MIN_HEIGHT;

/**
 * Hold a half-width to what map-svc accepts, and change nothing else about it.
 *
 * Fractional metres are kept: the service takes a double, and rounding to a
 * whole metre moved the number an imperial user had typed. 2,000 ft is 609.6 m,
 * which came back as 610 m and redisplayed as 2,001 ft.
 *
 * The non-finite fallback is a backstop for a value that never came from the
 * box — the control parses before it commits — so it can only answer in metres.
 */
export const clampLineHalfLengthMeters = (meters: number): number => {
  if (!Number.isFinite(meters)) return DEFAULT_LINE_HALF_LENGTH_METERS;
  return Math.min(
    MAX_LINE_HALF_LENGTH_METERS,
    Math.max(MIN_LINE_HALF_LENGTH_METERS, meters)
  );
};

/**
 * What each dashboard was last left with, for as long as the tab lives.
 *
 * A plain map rather than session storage. The card is not remembered across
 * a refresh - that is the ruling, and it matches Insights - so nothing here
 * has to survive the page, be validated on the way back in, or be kept out of
 * the next user's hands. Signing out clears it along with the store, because
 * this application signs out without reloading.
 */
const remembered = new Map<string, WellCardPanelRememberedState>();

// Spread over the defaults rather than handed back as it stands, so a slice
// written before a field existed comes back with that field at its default
// instead of at `undefined`.
const rememberedFor = (dashboardId: string): WellCardPanelRememberedState => ({
  ...defaultRememberedState,
  ...remembered.get(dashboardId)
});

const remember = (state: WellCardPanelState): void => {
  if (!state.dashboardId) return;
  remembered.set(state.dashboardId, {
    open: state.open,
    maximized: state.maximized,
    minimized: state.minimized,
    activeTab: state.activeTab,
    gunbarrelView: state.gunbarrelView,
    lineHalfLengthMeters: state.lineHalfLengthMeters,
    targetUwi: state.targetUwi,
    position: state.position,
    panelWidth: state.panelWidth,
    panelHeight: state.panelHeight
  });
};

export const useWellCardPanelStore = create<WellCardPanelStore>()((set, get) => {
  // Every remembered-field write goes through here, so what the dashboard
  // gets back cannot drift from what is on screen.
  const setAndRemember = (patch: Partial<WellCardPanelState>) => {
    set(patch);
    remember(get());
  };

  // A preview on the card's own well keeps the outline. Moving back to that
  // well does not bring it back.
  const outlineReleasedBy = (preview: Maybe<WellCardPreview>): boolean => {
    const uwi = previewUwi(preview);
    return get().outlineReleased || (!!uwi && uwi !== get().targetUwi);
  };

  return {
    ...initialState,

    // Switching dashboards loads the next dashboard's own card — its open flag,
    // its tab, and its well. Only the fields that describe the card's live
    // session are dropped: they mean nothing on the next dashboard, and the
    // drawn line belongs to a map view that is no longer on screen.
    hydrate: (dashboardId) => {
      if (get().dashboardId === dashboardId) return;
      // Put the dashboard being left back in the drawer before opening the
      // next one's, or switching away and back would lose whatever was not
      // written since the last change.
      remember(get());
      set({
        dashboardId,
        preview: null,
        outlineReleased: false,
        customLineActive: false,
        drawLineRequestId: 0,
        drawLineCancelId: 0,
        drawArmed: false,
        screenshotEmbed: null,
        screenshotNode: null,
        // Carries targetUwi as well, so a dashboard with no card of its own
        // loads a null well rather than keeping the last dashboard's.
        ...rememberedFor(dashboardId)
      });
    },

    /*
     * Closing drops the custom line so the next open derives afresh. Opening
     * undocks: asking for the card means asking to see it, and a card that
     * answered by lighting up a bar at the foot of the screen has not been
     * opened.
     *
     * A card that was not open comes back at its default place on the right,
     * whatever the last one on this dashboard was dragged to. Asking for a
     * card is asking for a new one, and finding it parked wherever a card was
     * left an hour ago reads as the app having lost it. Every other way back
     * keeps the position: restoring from the dock, coming out of fullscreen,
     * returning to the dashboard, and opening another well in a card that is
     * already up.
     */
    setOpen: (open) => {
      const freshOpen = open && !get().open;
      // Closing with a draw armed stands it down as well as unlighting the
      // button: the map is otherwise left waiting for two clicks with nothing
      // on screen that asked for them.
      const standDownDraw = !open && get().drawArmed;
      setAndRemember({
        open,
        minimized: open ? false : get().minimized,
        // The width and the height are deliberately left alone here, unlike
        // the position. They are not the same kind of thing: where a card sits
        // is particular to the card that was put there, and a new one arriving
        // on a spot the user cannot see he chose reads as the app having lost
        // it - but how big a card has to be to read comfortably is a setting,
        // and one the user set on purpose. Resetting it would make him drag the
        // edge out again on every open.
        position: freshOpen ? null : get().position,
        outlineReleased: freshOpen ? false : get().outlineReleased,
        customLineActive: open ? get().customLineActive : false,
        drawArmed: open ? get().drawArmed : false,
        drawLineCancelId: standDownDraw
          ? get().drawLineCancelId + 1
          : get().drawLineCancelId
      });
    },

    toggleMaximized: () => setAndRemember({ maximized: !get().maximized }),

    setMinimized: (minimized) => setAndRemember({ minimized }),

    undock: (maximized) =>
      setAndRemember({ minimized: false, maximized: maximized ?? get().maximized }),

    setPosition: (position) => setAndRemember({ position }),

    setPanelWidth: (width) => setAndRemember({ panelWidth: clampPanelWidth(width) }),

    setPanelHeight: (height) => setAndRemember({ panelHeight: clampPanelHeight(height) }),

    setActiveTab: (activeTab) => setAndRemember({ activeTab }),

    setGunbarrelView: (gunbarrelView) => setAndRemember({ gunbarrelView }),

    setLineHalfLengthMeters: (meters) =>
      setAndRemember({ lineHalfLengthMeters: clampLineHalfLengthMeters(meters) }),

    // A drawn line belongs to the well it was drawn for, and a well picked for
    // the card gets the outline back.
    //
    // Undocks for the same reason opening does: a new well is a new card to
    // read, and a docked one would put it in the bar and leave it there. Here
    // rather than at the callers - View Well Card, the data table's rows and
    // the well list all retarget, and the one that forgot would be a card
    // silently sitting on the wrong well.
    setTargetUwi: (targetUwi) =>
      setAndRemember({
        targetUwi,
        minimized: false,
        customLineActive: false,
        outlineReleased: false
      }),

    setPreview: (preview) =>
      set({
        preview,
        previewOffScreen: false,
        outlineReleased: outlineReleasedBy(preview)
      }),
    setPreviewOffScreen: (previewOffScreen) => set({ previewOffScreen }),

    // Clamped rather than wrapped: the arrows are disabled at the ends, so an
    // out-of-range index can only come from a race, and landing on the last
    // well the user was reading beats jumping to the other end of the list.
    setPreviewIndex: (index) => {
      const preview = get().preview;
      if (!preview) return;
      const activeIndex = Math.min(Math.max(index, 0), preview.candidates.length - 1);
      const paged = { ...preview, activeIndex };
      set({ preview: paged, outlineReleased: outlineReleasedBy(paged) });
    },

    releaseOutline: () => set({ outlineReleased: true }),

    requestLineDraw: () => set({ drawLineRequestId: get().drawLineRequestId + 1 }),

    cancelLineDraw: () => set({ drawLineCancelId: get().drawLineCancelId + 1 }),

    setDrawArmed: (drawArmed) => set({ drawArmed }),

    setScreenshotEmbed: (embed, node) => {
      if (node) {
        set({ screenshotEmbed: embed, screenshotNode: node });
        return;
      }
      if (get().screenshotEmbed !== embed) return;
      set({ screenshotEmbed: null, screenshotNode: null });
    },

    setCustomLineActive: (customLineActive) => set({ customLineActive }),

    // The publisher hands over a fresh object on every chart settings change;
    // an equal one must not refetch the array.
    setChartNormalizeBy: (normalizeBy) => {
      if (dequal(get().chartNormalizeBy, normalizeBy)) return;
      set({ chartNormalizeBy: normalizeBy });
    },

    setChartPreset: (chartPreset) => set({ chartPreset }),

    reset: () => {
      // Signing out has to forget every dashboard, not just the one on
      // screen: this application signs out without reloading the page.
      remembered.clear();
      set(initialState);
    }
  };
});

// ── Selectors ────────────────────────────────────────────────────────────────

export const selectWellCardPanelOpen = (s: WellCardPanelStore): boolean => s.open;
export const selectWellCardPanelMaximized = (s: WellCardPanelStore): boolean =>
  s.maximized;
export const selectWellCardPanelMinimized = (s: WellCardPanelStore): boolean =>
  s.minimized;
// Closing or docking the card keeps its maximized flag, so test all three.
export const selectWellCardPanelMaximizedOnScreen = (s: WellCardPanelStore): boolean =>
  s.open && s.maximized && !s.minimized;
export const selectWellCardPanelPosition = (
  s: WellCardPanelStore
): Maybe<PanelPosition> => s.position;
export const selectWellCardPanelWidth = (s: WellCardPanelStore): number => s.panelWidth;
export const selectWellCardPanelHeight = (s: WellCardPanelStore): Maybe<number> =>
  s.panelHeight;
export const selectWellCardPanelTab = (s: WellCardPanelStore): WellCardPanelTab =>
  s.activeTab;
export const selectWellCardGunbarrelView = (s: WellCardPanelStore): GunbarrelView =>
  s.gunbarrelView;
export const selectWellCardLineHalfLength = (s: WellCardPanelStore): Maybe<number> =>
  s.lineHalfLengthMeters;
export const selectWellCardTargetUwi = (s: WellCardPanelStore): Maybe<string> =>
  s.targetUwi;
export const selectWellCardPreview = (s: WellCardPanelStore): Maybe<WellCardPreview> =>
  s.preview;
// A popup hidden off screen counts as closed, so Escape goes to the map.
export const selectWellCardPreviewOpen = (s: WellCardPanelStore): boolean =>
  !!s.preview && !s.previewOffScreen;
// The map's outline and the jump glow both read this, so they cannot disagree
// on which well the card is highlighting.
export const selectWellCardOutlineUwi = (s: WellCardPanelStore): Maybe<string> =>
  previewUwi(s.preview) ?? (s.open && !s.outlineReleased ? s.targetUwi : null);
export const selectWellCardCustomLineActive = (s: WellCardPanelStore): boolean =>
  s.customLineActive;
export const selectWellCardDrawArmed = (s: WellCardPanelStore): boolean => s.drawArmed;
export const selectWellCardScreenshotNode = (
  s: WellCardPanelStore
): Maybe<HtmlPortalNode> => s.screenshotNode;
export const selectWellCardChartNormalizeBy = (
  s: WellCardPanelStore
): Maybe<INormalizeByInput> => s.chartNormalizeBy;
